import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useCallback, useRef } from "react";
import { Header } from "@/components/layout/header";
import { Main } from "@/components/layout/main";
import { ProfileDropdown } from "@/components/profile-dropdown";
import { SchedulePostButton } from "@/components/schedule-post-button";
import { useAuth } from '@/features/auth/auth-context';
import { supabase } from "@/lib/supabaseClient";
import { Globe, Lock, Calendar, CheckCircle2, Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CORSImage } from "@/components/ui/cors-image";
import YoutubeLogo from "@/assets/yt-logo.svg";
import { motion } from 'framer-motion';

type Upload = {
  id: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  visibility: string;
  status: string;
  youtube_video_id: string | null;
  youtube_url: string | null;
  scheduled_time: string | null;
  published_at: string | null;
  created_at: string;
  cached_view_count: number;
  youtube_channel_id: string | null;
};

export const Route = createFileRoute('/_authenticated/your-videos/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { user, loading: authLoading } = useAuth();
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPlatform, setFilterPlatform] = useState("all");
  const hasInitializedRef = useRef(false);

  const fetchUploads = useCallback(async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      const { data: userToken } = await supabase
        .from('youtube_tokens')
        .select('channel_id')
        .eq('user_id', user.id)
        .single();

      if (!userToken?.channel_id) {
        console.warn('No YouTube channel connected');
        setUploads([]);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('uploads')
        .select('*')
        .eq('user_id', user.id)
        .eq('youtube_channel_id', userToken.channel_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUploads(data || []);
    } catch (error) {
      console.error('Error fetching uploads:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      fetchUploads();
    }
  }, [user?.id, fetchUploads]);

  const getStatusDetails = (upload: Upload) => {
    if (upload.status === 'scheduled' && upload.scheduled_time) {
      const scheduledDate = new Date(upload.scheduled_time);
      if (scheduledDate <= new Date()) {
        return {
          label: 'Published',
          icon: <CheckCircle2 className="w-4 h-4 text-green-500" />,
          color: 'text-green-500'
        };
      }
      return {
        label: 'Scheduled',
        icon: <Clock className="w-4 h-4 text-blue-500" />,
        color: 'text-blue-500'
      };
    }
    
    if (upload.status === 'published') {
      return {
        label: 'Published',
        icon: <CheckCircle2 className="w-4 h-4 text-green-500" />,
        color: 'text-green-500'
      };
    }

    return {
      label: 'Draft',
      icon: <Calendar className="w-4 h-4 text-yellow-500" />,
      color: 'text-yellow-500'
    };
  };

  const getVisibilityIcon = (visibility: string) => {
    if (visibility === 'public') {
      return <Globe className="w-4 h-4" />;
    }
    return <Lock className="w-4 h-4" />;
  };

  const formatDate = (upload: Upload) => {
    if (upload.status === 'scheduled' && upload.scheduled_time) {
      return new Date(upload.scheduled_time).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
    if (upload.published_at) {
      return new Date(upload.published_at).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
    return new Date(upload.created_at).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredUploads = uploads.filter(upload => {
    const statusMatch = filterStatus === 'all' || upload.status === filterStatus;
    const platformMatch = filterPlatform === 'all' || filterPlatform === 'youtube';
    return statusMatch && platformMatch;
  });

  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <>
      <Header>
        <div className="ml-auto flex items-center space-x-4">
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className="flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-medium">Your Uploads</h1>
            <SchedulePostButton onAllowSchedule={() => {}} />
          </div>

          <div className="mb-6">
            <div className=" rounded-lg py-4 px-2">
              <div className="flex items-center gap-4">
                <div>
                  <label className="text-sm text-white/60 mb-2 block">Uploads</label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All uploads</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1">
                  <label className="text-sm text-white/60 mb-2 block">Platform</label>
                  <Select value={filterPlatform} onValueChange={setFilterPlatform}>
                    <SelectTrigger className="">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All platforms</SelectItem>
                      <SelectItem value="youtube">YouTube</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          
            <div className="grid grid-cols-[4fr,1fr,1fr,1fr,0.5fr,1fr] gap-4 px-4 py-4 text-white font-medium border-b border-white/20">
              <div>Video</div>
              <div>Visibility</div>
              <div>Upload Date</div>
              <div>Status</div>
              <div>Views</div>
              <div></div>
            </div>
        

          <div >
            {filteredUploads.length === 0 ? (
              <div className="text-center py-20 text-white/60">
                <p>No uploads found</p>
              </div>
            ) : (
              filteredUploads.map((upload, index) => {
                const statusDetails = getStatusDetails(upload);
                
                return (
                  <motion.div
                    key={upload.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      duration: 0.3,
                      delay: index * 0.05,
                      ease: "easeOut"
                    }}
                    className="grid grid-cols-[4fr,1fr,1fr,1fr,0.5fr,1fr] gap-4 px-4 py-3  hover:bg-[#1a1a1a] transition-colors items-center border-b border-white/20"
                  >
                    <div className="flex  gap-4">
                      <div 
                        className="relative w-40 rounded overflow-hidden flex-shrink-0 bg-[#0a0a0a]"
                        style={{ aspectRatio: '16/9' }}
                      >
                        {upload.thumbnail_url ? (
                          <>
                            <CORSImage
                              src={upload.thumbnail_url}
                              alt={upload.title}
                              className="w-full h-full object-cover"
                            />
                          
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white/20">
                            No thumbnail
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 max-w-[475px]">
                        <h3 className="font-medium text-white truncate mb-1">
                          {upload.title || 'Untitled'}
                        </h3>
                        <p className="text-sm text-white/60 line-clamp-2">
                          {upload.description || 'No description'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ">
                      {getVisibilityIcon(upload.visibility || 'unlisted')}
                      <span className="capitalize">{upload.visibility || 'Unlisted'}</span>
                    </div>

                    <div className="text-white">
                      {formatDate(upload)}
                    </div>

                    <div className="flex items-center gap-2">
                      {statusDetails.icon}
                      <span>{statusDetails.label}</span>
                    </div>

                    <div className="text-white">
                      {upload.status === 'published' ? (
                        upload.cached_view_count > 0 
                          ? upload.cached_view_count.toLocaleString() 
                          : 'Analytics coming soon'
                      ) : (
                        <span className="text-white/40">-</span>
                      )}
                    </div>

                    {/* View on YouTube Button */}
                    <div className="flex justify-end">
                      {upload.status === 'published' && upload.youtube_url ? (
                        <a 
                          href={upload.youtube_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-white text-black rounded-md text-sm font-medium hover:bg-white/90 transition-colors"
                        >
                          View on YouTube
                        </a>
                      ) : null}
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      </Main>
    </>
  );
}